import {
  deploymentTierSchema,
  prohibitedAutomationHosts,
  type DeploymentTier,
} from '@apuestapr/contracts';

import { resolveApiBaseUrl } from '../api/base-url.ts';

export const publicWebConfigVariables = [
  'VITE_PUBLIC_API_URL',
  'VITE_ADMIN_API_URL',
  'VITE_DEPLOYMENT_TIER',
  'VITE_CANONICAL_ORIGIN',
  'VITE_MAPBOX_ACCESS_TOKEN',
  'VITE_REGISTRATION_URL',
  'VITE_PLAY_URL',
] as const;

export interface PublicWebEnv {
  readonly DEV: boolean;
  readonly VITE_PUBLIC_API_URL?: string | undefined;
  readonly VITE_ADMIN_API_URL?: string | undefined;
  readonly VITE_DEPLOYMENT_TIER?: string | undefined;
  readonly VITE_CANONICAL_ORIGIN?: string | undefined;
  readonly VITE_MAPBOX_ACCESS_TOKEN?: string | undefined;
  readonly VITE_REGISTRATION_URL?: string | undefined;
  readonly VITE_PLAY_URL?: string | undefined;
}

export interface PublicWebConfigOptions {
  /** Injected to keep parsing deterministic and to validate the page host before any request. */
  readonly currentOrigin: string;
}

export interface PublicWebConfig {
  readonly deploymentTier: DeploymentTier;
  readonly publicApiOrigin: string;
  readonly adminApiOrigin: string;
  readonly canonicalOrigin: string;
  readonly mapboxAccessToken: string | null;
  readonly registrationUrl: string;
  readonly playUrl: string;
}

const guidance = 'Use an absolute origin with no credentials, path, query, or fragment.';

const parseOrigin = (variable: string, value: string): URL => {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error(`${variable} must be an absolute origin. ${guidance}`);
  }
  if (
    (url.protocol !== 'https:' && url.protocol !== 'http:') ||
    url.username !== '' ||
    url.password !== '' ||
    url.pathname !== '/' ||
    url.search !== '' ||
    url.hash !== ''
  ) {
    throw new Error(`${variable} is not an origin. ${guidance}`);
  }
  return url;
};

const isProhibited = (hostname: string): boolean =>
  (prohibitedAutomationHosts as readonly string[]).includes(hostname.toLowerCase());

const assertAllowedPageHost = (variable: string, url: URL, tier: DeploymentTier): void => {
  if (tier !== 'production' && isProhibited(url.hostname)) {
    throw new Error(`${variable} targets an owner-prohibited host in ${tier}.`);
  }
};

const requireHttpsWhenDeployed = (variable: string, url: URL, tier: DeploymentTier): void => {
  if (tier !== 'development' && url.protocol !== 'https:') {
    throw new Error(`${variable} must use HTTPS in the ${tier} tier.`);
  }
};

const isReservedHostname = (hostname: string): boolean =>
  hostname === 'test' ||
  hostname.endsWith('.test') ||
  hostname === 'invalid' ||
  hostname.endsWith('.invalid');

const parseDestination = (
  variable: string,
  value: string | undefined,
  tier: DeploymentTier,
): string => {
  const declared = value?.trim() ?? '';
  if (declared === '') throw new Error(`${variable} is required.`);

  let url: URL;
  try {
    url = new URL(declared);
  } catch {
    throw new Error(`${variable} must be an absolute HTTPS URL.`);
  }
  if (url.protocol !== 'https:' || url.username !== '' || url.password !== '') {
    throw new Error(`${variable} must be an HTTPS URL without credentials.`);
  }
  if (tier !== 'production' && !isReservedHostname(url.hostname)) {
    throw new Error(`${variable} must use a reserved .test or .invalid host in ${tier}.`);
  }
  if (tier !== 'production' && isProhibited(url.hostname)) {
    throw new Error(`${variable} targets an owner-prohibited host in ${tier}.`);
  }
  return url.href;
};

const parseTier = (env: PublicWebEnv): DeploymentTier => {
  const declared = env.VITE_DEPLOYMENT_TIER?.trim();
  if (declared === undefined || declared === '') {
    if (env.DEV) return 'development';
    throw new Error('VITE_DEPLOYMENT_TIER is required in a deployed build.');
  }
  const tier = deploymentTierSchema.parse(declared);
  if (env.DEV && tier !== 'development') {
    throw new Error('VITE_DEPLOYMENT_TIER must be development while Vite DEV is true.');
  }
  return tier;
};

export const parsePublicWebConfig = (
  env: PublicWebEnv,
  { currentOrigin }: PublicWebConfigOptions,
): PublicWebConfig => {
  const deploymentTier = parseTier(env);
  const isDevelopment = deploymentTier === 'development';

  const pageOrigin = parseOrigin('current page origin', currentOrigin);
  assertAllowedPageHost('current page origin', pageOrigin, deploymentTier);
  requireHttpsWhenDeployed('current page origin', pageOrigin, deploymentTier);

  const publicApiOrigin = resolveApiBaseUrl({
    variableName: 'VITE_PUBLIC_API_URL',
    value: env.VITE_PUBLIC_API_URL,
    isDevelopment,
  });
  const adminApiOrigin = resolveApiBaseUrl({
    variableName: 'VITE_ADMIN_API_URL',
    value: env.VITE_ADMIN_API_URL,
    isDevelopment,
  });
  for (const [variable, origin] of [
    ['VITE_PUBLIC_API_URL', publicApiOrigin],
    ['VITE_ADMIN_API_URL', adminApiOrigin],
  ] as const) {
    if (origin === '') continue;
    const url = parseOrigin(variable, origin);
    requireHttpsWhenDeployed(variable, url, deploymentTier);
  }

  const canonicalDeclared = env.VITE_CANONICAL_ORIGIN?.trim();
  if (!isDevelopment && (canonicalDeclared === undefined || canonicalDeclared === '')) {
    throw new Error('VITE_CANONICAL_ORIGIN is required in a deployed build.');
  }
  const canonical = parseOrigin(
    'VITE_CANONICAL_ORIGIN',
    canonicalDeclared === undefined || canonicalDeclared === ''
      ? pageOrigin.origin
      : canonicalDeclared,
  );
  requireHttpsWhenDeployed('VITE_CANONICAL_ORIGIN', canonical, deploymentTier);
  assertAllowedPageHost('VITE_CANONICAL_ORIGIN', canonical, deploymentTier);

  const mapboxToken = env.VITE_MAPBOX_ACCESS_TOKEN?.trim() ?? '';
  if (!isDevelopment && mapboxToken === '') {
    throw new Error('VITE_MAPBOX_ACCESS_TOKEN is required in a deployed build.');
  }

  return Object.freeze({
    deploymentTier,
    publicApiOrigin,
    adminApiOrigin,
    canonicalOrigin: canonical.origin,
    mapboxAccessToken: mapboxToken === '' ? null : mapboxToken,
    registrationUrl: parseDestination(
      'VITE_REGISTRATION_URL',
      env.VITE_REGISTRATION_URL,
      deploymentTier,
    ),
    playUrl: parseDestination('VITE_PLAY_URL', env.VITE_PLAY_URL, deploymentTier),
  });
};
